import React from "react";
import { ConnectionTypeEnum } from "src/components/Premises/types";
import { ListItem, List } from "@origin-digital/ods-core";

type SubsetConnectionGasTypes = Extract<
  ConnectionTypeEnum,
  | ConnectionTypeEnum.SERVICE_CONNECTION
  | ConnectionTypeEnum.METER_FIX
  | ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX
>;
type SubsetConnectionElectricityTypes = Extract<
  ConnectionTypeEnum,
  | ConnectionTypeEnum.PERMANENT
  | ConnectionTypeEnum.TEMPORARY
  | ConnectionTypeEnum.TEMPPERM
>;

export type AggregatedElectricityConnectionTypes =
  | SubsetConnectionGasTypes
  | "ELECTRICITY";

export type AggregatedGasConnectionTypes =
  | SubsetConnectionElectricityTypes
  | "GAS";

export type AggregatedConnectionTypes = "ELECTRICITY" | "GAS";

export const getTimeframes = ({
  postcode,
}: {
  postcode: string;
}): Record<
  string,
  Partial<Record<AggregatedElectricityConnectionTypes, React.ReactNode>>
> => {
  const isExceptionPostcode = getIsExceptionPostcode(postcode);
  return {
    ACT: {
      ELECTRICITY: (
        <ListItem key={"ELECTRICITY"}>
          The electricity meter provider will install the meter and turn the
          power on. There may be delays if the request is inaccurate or is
          missing information.
        </ListItem>
      ),
      [ConnectionTypeEnum.SERVICE_CONNECTION]: (
        <ListItem key={"SERVICE_CONNECTION"}>
          When the site is clean and ready the distributor will install the gas
          service line. This can take six to eight weeks. Normally the gas meter
          will be fitted at the same time.
        </ListItem>
      ),
      [ConnectionTypeEnum.METER_FIX]: (
        <ListItem key={"METER_FIX"}>
          When the site is clean and ready the distributor will install the gas
          service line. This can take six to eight weeks. Normally the gas meter
          will be fitted at the same time.
        </ListItem>
      ),
      [ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX]: (
        <ListItem key={"SERVICE_CONNECTION_AND_METER_FIX"}>
          When the site is clean and ready the distributor will install the gas
          service line. This can take six to eight weeks. Normally the gas meter
          will be fitted at the same time.
        </ListItem>
      ),
    },
    NSW: {
      ELECTRICITY: (
        <ListItem key={"ELECTRICITY"}>
          The meter provider will install the electricity meter and turn the
          power on. There may be delays if the request is inaccurate or is
          missing information.
        </ListItem>
      ),
      [ConnectionTypeEnum.SERVICE_CONNECTION]: isExceptionPostcode
        ? VIC_SERVICE_CONNECTION_TIMEFRAMES
        : NSW_SERVICE_CONNECTION_TIMEFRAMES,
      [ConnectionTypeEnum.METER_FIX]: isExceptionPostcode
        ? VIC_METER_FIX_TIMEFRAMES
        : NSW_METER_FIX_TIMEFRAMES,
      [ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX]: (
        <ListItem key={"SERVICE_CONNECTION_AND_METER_FIX"}>
          When the site is clean and ready the distributor will install the gas
          path valve. This can take six to eight weeks. Normally the gas meter
          kit will be fitted at the same time.
          <List variant="body-small" space="xsmall">
            <ListItem>
              Important: some jobs may incur extra costs. If the job needs a
              quote, we’ll be in touch to set up a time for the distributor to
              assess the work involved. This could take up to 20 working days
              for residential connections, and up to 65 working days for
              commercial connections. The quote will include a timeframe to
              complete the work. Work can’t start until the costs are paid in
              full.
            </ListItem>
          </List>
        </ListItem>
      ),
    },
    QLD: {
      ELECTRICITY: (
        <ListItem key={"ELECTRICITY"}>
          The meter provider will install the electricity meter and turn the
          power on. There may be delays if the request is inaccurate or is
          missing information.
        </ListItem>
      ),
      [ConnectionTypeEnum.SERVICE_CONNECTION]: (
        <ListItem key={"SERVICE_CONNECTION"}>
          When the planner has approved the site, the gas service line will
          usually be installed within 30 working days, although weather and
          availability of crew may impact these timings.
          <List variant="body-small" space="xsmall">
            <ListItem>
              Important: some jobs may incur extra costs. If the job needs a
              quote, we’ll be in touch to set up a time for the distributor to
              assess the work involved. This could take up to 20 working days
              for residential connections, and up to 40 working days for
              commercial connections. The quote will include a timeframe to
              complete the work. Work can’t start until the costs are paid in
              full.
            </ListItem>
          </List>
        </ListItem>
      ),
      [ConnectionTypeEnum.METER_FIX]: (
        <ListItem key={"METER_FIX"}>
          The distributor will have the gas meter installed, usually within
          two-five working days. There may be delays if the request is
          inaccurate or is missing information.
        </ListItem>
      ),
      [ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX]: (
        <ListItem key={"SERVICE_CONNECTION_AND_METER_FIX"}>
          When the planner has approved the site, the gas service line will
          usually be installed within 30 working days, although weather and
          availability of crew may impact these timings. The distributor will
          have the gas meter installed, usually within another two working days.
          <List variant="body-small" space="xsmall">
            <ListItem>
              Important: some jobs may incur extra costs. If the job needs a
              quote, we’ll be in touch to set up a time for the distributor to
              assess the work involved. This could take up to 20 working days
              for residential connections, and up to 40 working days for
              commercial connections. The quote will include a timeframe to
              complete the work. Work can’t start until the costs are paid in
              full.{" "}
            </ListItem>
          </List>
        </ListItem>
      ),
    },
    SA: {
      ELECTRICITY: (
        <ListItem key={"ELECTRICITY"}>
          The electricity meter provider will install the meter and turn the
          power on. There may be delays if the request is inaccurate or is
          missing information.
        </ListItem>
      ),
      [ConnectionTypeEnum.SERVICE_CONNECTION]: (
        <ListItem key={"SERVICE_CONNECTION"}>
          When the planner has approved the site, the gas service line will
          usually be installed within 30 working days, although weather and
          availability of crew may impact these timings.
          <List variant="body-small" space="xsmall">
            <ListItem>
              Important: some jobs may incur extra costs. If the job needs a
              quote, we’ll be in touch to set up a time for the distributor to
              assess the work involved. This could take up to 20 working days
              for residential connections, and up to 40 working days for
              commercial connections. The quote will include a timeframe to
              complete the work. Work can’t start until the costs are paid in
              full.
            </ListItem>
          </List>
        </ListItem>
      ),
      [ConnectionTypeEnum.METER_FIX]: (
        <ListItem key={"METER_FIX"}>
          The distributor will endeavour to install the gas meter based on the
          appointment date and time provided on the request, although weather
          and available of crew may impact these timings.
        </ListItem>
      ),
    },
    VIC: {
      ELECTRICITY: (
        <ListItem key={"ELECTRICITY"}>
          The distributor will install the electricity meter and turn the power
          on. Allow up to 12 working days from the day the request is received
          for this to happen. There may be delays if the request is inaccurate
          or is missing information.
        </ListItem>
      ),
      [ConnectionTypeEnum.SERVICE_CONNECTION]:
        VIC_SERVICE_CONNECTION_TIMEFRAMES,
      [ConnectionTypeEnum.METER_FIX]: VIC_METER_FIX_TIMEFRAMES,
    },
  };
};

const EXCEPTION_POSTCODES = [
  "2640",
  "2641",
  "2642",
  "2643",
  "2647",
  "2712",
  "2713",
  "2714",
  "2731",
];

const getIsExceptionPostcode = (postcode: string) => {
  return EXCEPTION_POSTCODES.includes(postcode);
};

const VIC_SERVICE_CONNECTION_TIMEFRAMES = (
  <ListItem key={"SERVICE_CONNECTION"}>
    When the planner has approved the site, the gas service line will usually be
    installed within 20 working days, although weather and availability of crew
    may impact these timings.
    <List variant="body-small" space="xsmall">
      <ListItem>
        Important: some jobs may incur extra costs. If the job needs a quote,
        we’ll be in touch to set up a time for the distributor to assess the
        work involved. This could take up to 20 working days for residential
        connections, and up to 40 working days for commercial connections. The
        quote will include a timeframe to complete the work. Work can’t start
        until the costs are paid in full.
      </ListItem>
    </List>
  </ListItem>
);

const VIC_METER_FIX_TIMEFRAMES = (
  <ListItem key={"SERVICE_CONNECTION"}>
    The distributor will have the gas meter installed, usually within 2-5
    working days from the day the request is received for this to happen. There
    may be delays if the request is inaccurate or is missing information.
    <List variant="body-small" space="xsmall">
      <ListItem>
        Important: some jobs may incur extra costs. If the job needs a quote,
        we'll be in touch to set up a time for the distributor to assess the
        work involved. This could take up to 20 working days for residential
        connections, and up to 40 working days for commercial connections. The
        quote will include a timeframe to complete the work. Work can’t start
        until the costs are paid in full.
      </ListItem>
    </List>
  </ListItem>
);

const NSW_SERVICE_CONNECTION_TIMEFRAMES = (
  <ListItem key={"SERVICE_CONNECTION"}>
    When the site is clean and ready the distributor will install the gas path
    valve. This can take six to eight weeks.
    <List variant="body-small" space="xsmall">
      <ListItem>
        Important: some jobs may incur extra costs. If the job needs a quote,
        we’ll be in touch to set up a time for the distributor to assess the
        work involved. This could take up to 20 working days for residential
        connections, and up to 65 working days for commercial connections. The
        quote will include a timeframe to complete the work. Work can’t start
        until the costs are paid in full.
      </ListItem>
    </List>
  </ListItem>
);

const NSW_METER_FIX_TIMEFRAMES = (
  <ListItem key={"METER_FIX"}>
    Your plumber will arrange the pick-up and installation of the meter kit with
    the distributor.
  </ListItem>
);
